import { useMemo, useContext } from 'react';
import {
  AgreementType,
  CurrentUserTypeSource,
  UserSource,
  OrgAuditType,
  AgreementPriceMissingReason,
  NetPriceSourceEnum,
} from 'libs/enums';
import { UserContext } from 'libs/contexts';

export default function useDetailPriceAndAgreement(
  skuDetail,
  updatePriceLoading = false,
  selectTradeAgreement,
) {
  const userInfo = useContext(UserContext);
  //交易规则下拉框,过滤出通用交易规则
  const differentTypesAgreement = useMemo(() => {
    return {
      origin: skuDetail?.tradeAgreementEntityList || [],
      alternative:
        skuDetail?.tradeAgreementEntityList?.filter(
          (v) => v.tradeAgreementScope !== AgreementType.beCurrent,
        ) || [],
    };
  }, [skuDetail]);
  // 根据交易规则和用户类型判断商品价格是否显示，是否可售卖
  const agreeAndUserTypeIsAvailable = useMemo(() => {
    // 外部用户未认证,不允许下单
    if (
      userInfo?.organizationSource === UserSource.outside &&
      userInfo?.orgAuditType === OrgAuditType.unAudit
    ) {
      return false;
    }
    /*
          1.每一件商品正常情况下必须绑定交易规则
          2.未能查询到交易规则情况(用户未登录,历史数据商品未绑定交易规则),不允许下单
          3.只有对该用户生效的交易规则才会返回,其中商品如果绑定了通用交易规则也会返回,但通用交易规则不允许手动选择(differentTypesAgreement已过滤),通用交易规则只对外部用户生效
          4.返回的交易规则一定是对用户生效的,但内部用户会把通用交易规则返回,所以外部用户或个人用户无需判断交易规则类型,但内部用户需判断一下当前交易规则类型
        */
    if (!differentTypesAgreement.origin.length || !Object.keys(selectTradeAgreement || {}).length) {
      return false;
    }
    // 个人用户
    if (userInfo?.currentUserType === CurrentUserTypeSource.personal) {
      if (userInfo?.userAuditType) {
        return true;
      }
      return false;
    }
    /*
          1.内部用户 
          外部交易规则(不可售卖)
          2.外部用户 (可售卖)
          tradeAgreementScope 交易规则类型（0外部，1内部）
          organizationSource: 0:外部 2:内部
        */
    const { tradeAgreementScope } = selectTradeAgreement;
    switch (userInfo?.organizationSource) {
      // 外部用户
      case UserSource.outside:
        return true;
      // 内部用户
      case UserSource.inside:
        return tradeAgreementScope === AgreementType.internal;
      default:
    }

    return false;
  }, [userInfo, selectTradeAgreement, differentTypesAgreement.origin]);

  const externalPurchaser =
    userInfo?.currentUserType === CurrentUserTypeSource.purchaser &&
    userInfo?.organizationSource === UserSource.outside;
  // 外部采购商是否认证
  const externalPurchaserAudit =
    userInfo?.orgAuditType === OrgAuditType.purchaser ||
    userInfo?.orgAuditType === OrgAuditType.supplierAndPurchaser;
  const skuForPriceCalculate = useMemo(() => {
    return selectTradeAgreement?.skuForPriceCalculate
      ? selectTradeAgreement?.skuForPriceCalculate[0]
      : {};
  }, [selectTradeAgreement]);
  // 价格是否可见,返回对应原因
  const isThePriceDisplayed = useMemo(() => {
    const result = {
      display: false,
      reason: '待询价',
    };
    // 正在更新价格
    if (updatePriceLoading) {
      result.reason = '正在获取网价';
      return result;
    }
    if (skuDetail?.salesPrice !== null) {
      // 已认证外部采购商,或已认证个人,取交易规则判断
      if (
        (externalPurchaser && externalPurchaserAudit) ||
        (userInfo?.currentUserType === CurrentUserTypeSource.personal && userInfo?.userAuditType)
      ) {
        result.display = Boolean(differentTypesAgreement.origin.length);
        return result;
      }
      result.display = skuDetail?.showPrice;
      return result;
    }

    // 当前选中的交易规则skuForPriceCalculate
    const {
      netPriceSource,
      purchaserCustomizePrice,
      noUnitConversionRate,
      agreementPriceMissingReason,
    } = skuForPriceCalculate;

    //  通过当前选中的交易规则判断商品价格展示文案
    if (purchaserCustomizePrice) result.reason = '由采购方自行填写价格';
    if (noUnitConversionRate) result.reason = '无理论重量，无法计算价格';

    if (agreementPriceMissingReason === AgreementPriceMissingReason.TRADE_AGREEMENT_PRICE_MISSING) {
      result.reason = '暂无协议价';
    }

    const { INFO, LANGE, WDGTW, SHYSJSW, BASE } = NetPriceSourceEnum;
    // netPriceSource,获取不到价格的原因，服务端返回枚举值，前端维护字典表对应展示
    switch (netPriceSource) {
      case INFO:
        result.reason = '获取不到信息价';
        break;
      case LANGE:
      case WDGTW:
      case SHYSJSW:
        result.reason = '获取不到网价';
        break;
      case BASE:
        result.reason = '获取不到基价';
        break;
      default:
        break;
    }

    return result;
  }, [
    updatePriceLoading,
    differentTypesAgreement,
    externalPurchaser,
    externalPurchaserAudit,
    skuDetail,
    skuForPriceCalculate,
    userInfo,
  ]);
  return {
    differentTypesAgreement,
    agreeAndUserTypeIsAvailable,
    isThePriceDisplayed,
    externalPurchaser,
    externalPurchaserAudit,
    skuForPriceCalculate,
  };
}
